import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import { CursosExternosService } from '../../../../service/cursosExternos.service';
import { FileService } from '../../../../service/file.service';
import { CompletitudCursoExterno, CursoExterno } from '../../../../service/types/cursosExternos.types';
import SignaturePad from '../../../pages/m4/predioPrograma/signaturePad';

const DECLARACION =
  'Declaro que he respondido este cuestionario de manera consciente, honesta y con pleno conocimiento ' +
  'de su propósito, asumiendo la responsabilidad por la veracidad de la información proporcionada.';

interface CursoExternoViewProps {
  cursoExternoId: string;
  onCompletado?: () => void;
  onCerrar?: () => void;
}

export default function CursoExternoView({ cursoExternoId, onCompletado, onCerrar }: CursoExternoViewProps) {
  const [curso, setCurso] = useState<CursoExterno | null>(null);
  const [progreso, setProgreso] = useState<CompletitudCursoExterno | null>(null);
  const [firma, setFirma] = useState('');
  const [archivoUrl, setArchivoUrl] = useState('');
  const [archivoNombre, setArchivoNombre] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const [cursoData, progresoData] = await Promise.all([
      CursosExternosService.findOne(cursoExternoId),
      CursosExternosService.getProgreso(cursoExternoId),
    ]);
    setCurso(cursoData);
    setProgreso(progresoData);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursoExternoId]);

  const handleArchivoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('files', file);
    setSubiendo(true);
    setError(null);
    try {
      const response = await FileService.upload(formData);
      setArchivoUrl(response.paths[0]);
      setArchivoNombre(file.name);
    } catch {
      setError('No se pudo subir el archivo. Intente nuevamente.');
    } finally {
      setSubiendo(false);
    }
  };

  const handleEnviar = async () => {
    if (!archivoUrl) {
      setError('Debe adjuntar un archivo (imagen o documento) como evidencia');
      return;
    }
    if (!firma) {
      setError('Debe dibujar su firma antes de continuar');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await CursosExternosService.completar(cursoExternoId, { firma, archivoUrl });
      await load();
      onCompletado?.();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'No se pudo enviar la evidencia. Intente nuevamente.');
    } finally {
      setEnviando(false);
    }
  };

  if (!curso) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" /> Cargando curso externo...
      </div>
    );
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>{curso.nombre}</span>
        <div className="d-flex align-items-center gap-2">
          <Badge bg={progreso ? 'success' : 'secondary'}>{progreso ? 'Completado' : 'Pendiente'}</Badge>
          {onCerrar && (
            <Button size="sm" variant="outline-secondary" onClick={onCerrar}>
              Volver
            </Button>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        {curso.descripcion && <p style={{ whiteSpace: 'pre-wrap' }}>{curso.descripcion}</p>}
        <Button variant="primary" href={curso.link} target="_blank" rel="noopener noreferrer" className="mb-3">
          Ir al curso externo
        </Button>

        {progreso && (
          <Alert variant="success">
            Completado el {new Date(progreso.completadoEn).toLocaleDateString()}.{' '}
            <a href={'/api' + progreso.archivoUrl} target="_blank" rel="noopener noreferrer">
              Ver evidencia enviada
            </a>
            . Puede volver a enviarla si lo necesita.
          </Alert>
        )}

        <hr />
        <p>{DECLARACION}</p>
        <SignaturePad onChange={setFirma} />

        <Form.Group className="mt-3 mb-3">
          <Form.Label>Evidencia de completitud (imagen o documento)</Form.Label>
          <Form.Control
            type="file"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={handleArchivoChange}
            disabled={subiendo}
          />
          {subiendo && (
            <div className="text-muted small mt-1">
              <Spinner animation="border" size="sm" /> Subiendo archivo...
            </div>
          )}
          {archivoNombre && !subiendo && <div className="text-success small mt-1">Archivo listo: {archivoNombre}</div>}
        </Form.Group>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="d-flex justify-content-end">
          <Button variant="success" onClick={handleEnviar} disabled={enviando || subiendo}>
            {enviando ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
